import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Spinner,
  Tooltip,
  Progress,
  chakra,
} from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React, { useState } from 'react';

import {
  finleyAssertUnreachable,
  path as commonPath,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  CashBalanceTape,
  CashBalanceTapeState,
  CreditFacilityMap,
  DataTape,
  DataTapeMap,
  DataTapeState,
  UploadCompletionMap,
} from 'types/manualDataTapeUpload';

import { DataTapeFormatModal } from './DataTapeFormatModal';
import {
  InfoIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { FiX } from 'react-icons/fi';

const displayUploadButton = (
  tape: DataTapeState | CashBalanceTapeState,
): boolean => {
  switch (tape.localStatus) {
    case 'uploading':
    case 'processing':
    case 'success':
      return false;
    case 'none':
    case 'error':
      return true;
    default:
      throw finleyAssertUnreachable(tape.localStatus);
  }
};

const uploadStatus = ({
  tape,
  selectedFileVersion,
  selectedReportingDate,
}: {
  tape: DataTapeState | CashBalanceTapeState;
  selectedReportingDate: Util.FullDateStr;
  selectedFileVersion: number;
}): boolean => {
  return (
    tape.currentFileVersion === selectedFileVersion &&
    tape.currentReportingDate === selectedReportingDate &&
    (tape.localStatus === 'none' || tape.localStatus === 'success')
  );
};

const getStatusIcon = (tape: DataTapeState | CashBalanceTapeState) => {
  if (tape.localStatus === 'success') {
    return (
      <>
        <Icon icon="circle-check" color="green" />
        <Text fontSize="sm" ml="10px">
          Upload Complete
        </Text>
      </>
    );
  }
  if (tape.localStatus === 'error') {
    return (
      <>
        <FiX color="red" />
        <Text fontSize="xs">Please see errors below.</Text>
      </>
    );
  }

  // TODO(jon): in-progress spinners are currently handled separately. Move that logic here.
  return <></>;
};

const RequiredDataTape: React.FC<{
  dataTape: DataTapeState;
  setModalDataTape: (dataTape: DataTape) => void;
  creditFacilities: CreditFacilityMap;
  selectedReportingDate: Util.FullDateStr;
  selectedFileVersion: number;
  loadingPercent: number;
}> = withLDConsumer()(
  ({
    dataTape,
    setModalDataTape,
    creditFacilities,
    selectedReportingDate,
    selectedFileVersion,
    loadingPercent,
    flags,
  }) => {
    const [isFormatModalOpen, setIsModalFormatOpen] = useState(false);
    const allowUpload = displayUploadButton(dataTape);
    const isValid = uploadStatus({
      tape: dataTape,
      selectedReportingDate,
      selectedFileVersion,
    });
    const statusIcon = getStatusIcon(dataTape);
    return (
      <Tr>
        <Td>
          <Text>{commonPath.basename(dataTape.externalFilename)}</Text>
          {dataTape.fileType === 'xlsx' && (
            <Box _notLast={{ mb: '8px' }}>
              <chakra.table fontSize="xs" width="100%">
                {dataTape.tapeFieldGroups.map(
                  ({ dataTapeSlug, sheetName, headerRange }) => (
                    <chakra.tr key={dataTapeSlug}>
                      <chakra.td
                        width="1%"
                        whiteSpace="nowrap"
                        paddingRight="8px"
                      >
                        <chakra.span fontWeight="bold">Sheet:</chakra.span>{' '}
                        {sheetName}
                      </chakra.td>
                      <chakra.td>
                        <chakra.span fontWeight="bold">Headers:</chakra.span>{' '}
                        {headerRange}
                      </chakra.td>
                    </chakra.tr>
                  ),
                )}
              </chakra.table>
            </Box>
          )}
          {dataTape.currentReportingDate &&
            !_.isNil(dataTape.currentFileVersion) && (
              // Extra <Box> is to support `_notLast` (which translates to :last-of-type) above.
              <Box>
                <Text fontSize="xs">{`Last uploaded v${dataTape.currentFileVersion} for reporting date ${dataTape.currentReportingDate}`}</Text>
              </Box>
            )}
        </Td>
        <Td>
          {_.map(dataTape.validCreditFacilityIds, (cf) => {
            if (creditFacilities && creditFacilities[cf])
              return (
                <Text key={`credit-facility-${creditFacilities[cf].id}`}>
                  {creditFacilities[cf].formattedSlug}
                </Text>
              );
            return '';
          })}
        </Td>
        <Td>
          <DataTapeFormatModal
            isOpen={isFormatModalOpen}
            onClose={() => setIsModalFormatOpen(false)}
            dataTape={dataTape}
          />
          <InfoIconButton
            ariaLabel="Data Tape Format"
            label="Data Tape Format"
            onClick={() => setIsModalFormatOpen(true)}
          />
        </Td>
        <Td textAlign="center">
          <>
            {allowUpload && (
              <UploadIconButton
                ariaLabel="Upload loan tape"
                label="Upload loan tape"
                fontSize="xl"
                onClick={() => {
                  setModalDataTape(dataTape);
                }}
              />
            )}
            {allowUpload && isValid && (
              <Tooltip
                label="A valid input has already been uploaded. Submitting a new version will invalidate the existing upload."
                placement="right"
              >
                <InfoOutlineIcon
                  position="absolute"
                  marginTop="12px"
                  marginLeft="10px"
                />
              </Tooltip>
            )}
            {dataTape.localStatus === 'uploading' &&
              !flags?.borrowerS3Upload && (
                <Spinner
                  thickness="1px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="sm"
                />
              )}
          </>
        </Td>
        <Td textAlign="center">
          {dataTape.localStatus !== 'uploading' && (
            <Flex justifyContent="center" alignItems="center">
              {statusIcon}
            </Flex>
          )}
          {dataTape.localStatus === 'uploading' && flags?.borrowerS3Upload && (
            <>
              <Text textAlign="left" fontSize="sm">{`${loadingPercent.toFixed(
                0,
              )}%`}</Text>
              <Progress
                value={loadingPercent}
                size="sm"
                colorScheme="green"
                borderRadius="5px"
                mt="5px"
              />
              <Text textAlign="left" fontSize="xs" mt="5px">
                Upload in progress
              </Text>
            </>
          )}
        </Td>
      </Tr>
    );
  },
);

const RequiredCashBalance: React.FC<{
  cashBalance: CashBalanceTapeState;
  creditFacilities: CreditFacilityMap;
  setModalCashBalance: (cashBalance: CashBalanceTape) => void;
  selectedReportingDate: Util.FullDateStr;
  selectedFileVersion: number;
}> = ({
  cashBalance,
  creditFacilities,
  setModalCashBalance,
  selectedReportingDate,
  selectedFileVersion,
}) => {
  const allowUpload = displayUploadButton(cashBalance);
  const isValid = uploadStatus({
    tape: cashBalance,
    selectedReportingDate,
    selectedFileVersion,
  });
  const statusIcon = getStatusIcon(cashBalance);
  const accountConnectionMessage = `
  An existing API integration will fetch the latest cash balance. To upload manually, in the Bank Accounts tab of your Settings, set the connection provider to None for the following
  ${
    cashBalance.accountTypesWithConnectionProvider.length > 1
      ? 'accounts'
      : 'account'
  }:
  ${_.map(cashBalance.accountTypesWithConnectionProvider, (type) =>
    _.lowerCase(type),
  ).join(', ')}`;
  return (
    <Tr>
      <Td>
        <Text>{commonPath.basename(cashBalance.externalFilename)}</Text>
        {cashBalance.currentReportingDate &&
          !_.isNil(cashBalance.currentFileVersion) && (
            <Text fontSize="xs">{`Last uploaded v${cashBalance.currentFileVersion} for reporting date ${cashBalance.currentReportingDate}`}</Text>
          )}
      </Td>
      <Td>
        <Text>
          {creditFacilities[cashBalance.creditFacilityId].formattedSlug}
        </Text>
      </Td>
      <Td>
        {/* data tape dictionary would appear in this column for non-cash tapes */}
      </Td>
      {cashBalance.accountTypesWithConnectionProvider.length > 0 && (
        <Td colSpan={2}>
          <Text fontSize="xs">{accountConnectionMessage}</Text>
        </Td>
      )}
      {cashBalance.accountTypesWithConnectionProvider.length === 0 && (
        <>
          <Td textAlign="center">
            <>
              {allowUpload && (
                <UploadIconButton
                  ariaLabel="Upload cash balance"
                  label="Upload cash balance"
                  fontSize="xl"
                  onClick={() => {
                    setModalCashBalance(cashBalance);
                  }}
                />
              )}
              {allowUpload && isValid && (
                <Tooltip
                  label="A valid input has already been uploaded. Submitting a new version will invalidate the existing upload."
                  placement="right"
                >
                  <InfoOutlineIcon
                    position="absolute"
                    marginTop="12px"
                    marginLeft="10px"
                  />
                </Tooltip>
              )}
              {cashBalance.localStatus === 'uploading' && (
                <Spinner
                  thickness="1px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="sm"
                />
              )}
            </>
          </Td>
          <Td textAlign="center">
            <Flex justifyContent="center" flexWrap="wrap">
              {statusIcon}
            </Flex>
          </Td>
        </>
      )}
    </Tr>
  );
};

export const RequiredDataTapesTable: React.FC<{
  dataTapes: DataTapeMap;
  cashBalances: CashBalanceTapeState[];
  setModalDataTape: (dataTape: DataTape) => void;
  setModalCashBalance: (cashBalance: CashBalanceTape) => void;
  currentCreditFacilityId: string | null;
  creditFacilitiesMap: CreditFacilityMap;
  selectedReportingDate: Util.FullDateStr;
  selectedFileVersion: number;
  uploadCompletionMap: UploadCompletionMap;
}> = ({
  dataTapes,
  cashBalances,
  setModalDataTape,
  setModalCashBalance,
  currentCreditFacilityId,
  creditFacilitiesMap,
  selectedReportingDate,
  selectedFileVersion,
  uploadCompletionMap,
}) => {
  const noCfFilter = currentCreditFacilityId === null;

  return (
    <Table mt="48px">
      <Thead>
        <Tr>
          <Th width="35%">Required Input</Th>
          <Th width="35%">Credit Facilities</Th>
          <Th width="10%">Data Tape Format</Th>
          <Th textAlign="center" width="5%">
            Upload
          </Th>
          <Th textAlign="center" width="15%">
            Status
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {_.map(dataTapes, (dataTape) => {
          return noCfFilter ||
            dataTape.validCreditFacilityIds.some(
              (id) => id === currentCreditFacilityId,
            ) ? (
            <RequiredDataTape
              dataTape={dataTape}
              key={dataTape.artifactSlug}
              setModalDataTape={setModalDataTape}
              creditFacilities={creditFacilitiesMap}
              selectedReportingDate={selectedReportingDate}
              selectedFileVersion={selectedFileVersion}
              loadingPercent={uploadCompletionMap[dataTape.key] ?? 0}
            />
          ) : null;
        })}
        {_.map(cashBalances, (cashBalance) => {
          return noCfFilter ||
            cashBalance.creditFacilityId === currentCreditFacilityId ? (
            <RequiredCashBalance
              cashBalance={cashBalance}
              key={cashBalance.artifactSlug}
              creditFacilities={creditFacilitiesMap}
              setModalCashBalance={setModalCashBalance}
              selectedReportingDate={selectedReportingDate}
              selectedFileVersion={selectedFileVersion}
            />
          ) : null;
        })}
      </Tbody>
    </Table>
  );
};
